import React, { useCallback, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';

/** Value is [left, right] with left <= right. Drag swap: when you drag one handle past the other, it stops and the other handle takes over. */
export type RangeSliderValue = [number, number];

export interface RangeSliderProps {
  /** Current values [left, right] with left <= right. When dragging past the other handle, the drag transfers to that handle. */
  value: RangeSliderValue;
  /** Called when user changes a handle. (newValue: [left, right]) => void */
  onChange: (value: RangeSliderValue) => void;
  /** Minimum value of the scale */
  min?: number;
  /** Maximum value of the scale */
  max?: number;
  /** Step for keyboard and optional step snapping */
  step?: number;
  /** Disabled state */
  disabled?: boolean;
  /** ARIA label for the left handle */
  ariaLabelLeft: string;
  /** ARIA label for the right handle */
  ariaLabelRight: string;
  /** ARIA label for the whole slider group (e.g. "Price range") */
  ariaLabelGroup?: string;
  /** Optional human-readable value for screen readers (left handle) */
  ariaValueTextLeft?: (value: number) => string;
  /** Optional human-readable value for screen readers (right handle) */
  ariaValueTextRight?: (value: number) => string;
  /** Class name for the root container */
  className?: string;
  /** Class name for the track (full-width bar container) */
  trackClassName?: string;
  /** Class name for the rail (background part of the track) */
  railClassName?: string;
  /** Class name for the range (filled area between the two handles) */
  rangeClassName?: string;
  /** Class name for the left handle (position-based) */
  thumbLeftClassName?: string;
  /** Class name for the right handle (position-based) */
  thumbRightClassName?: string;
  /** Fill color for the left handle (e.g. '#6F87F1'). Also used as start of range gradient. */
  thumbLeftColor?: string;
  /** Fill color for the right handle. Also used as end of range gradient. */
  thumbRightColor?: string;
  /** Tooltip text (or node) for the left handle; shown on hover and while dragging. */
  tooltipLeft?: React.ReactNode;
  /** Tooltip text (or node) for the right handle; shown on hover and while dragging. */
  tooltipRight?: React.ReactNode;
  /** Persistent label for the left handle; moves with the handle. */
  labelLeft?: React.ReactNode;
  /** Persistent label for the right handle; moves with the handle. */
  labelRight?: React.ReactNode;
  /**
   * When true, left handle: label renders above and tooltip below (vice versa of default).
   * Default false: label below handle, tooltip on top.
   */
  flipLabelPositionLeft?: boolean;
  /**
   * When true, right handle: label renders above and tooltip below (vice versa of default).
   * Default false: label below handle, tooltip on top.
   */
  flipLabelPositionRight?: boolean;
  /** Inline styles; same keys as class names */
  style?: {
    root?: React.CSSProperties;
    track?: React.CSSProperties;
    rail?: React.CSSProperties;
    range?: React.CSSProperties;
    thumbLeft?: React.CSSProperties;
    thumbRight?: React.CSSProperties;
    labelLeft?: React.CSSProperties;
    labelRight?: React.CSSProperties;
  };
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/**
 * Dual-thumb range slider with swap-on-cross: value is [left, right], left <= right.
 * When you drag the right handle past the left (or left past right), the dragged handle
 * stops at the other's position and the other handle "takes over" the drag.
 * Style the left and right handles via thumbLeftClassName / thumbRightClassName or style.thumbLeft / style.thumbRight.
 */
export const RangeSlider: React.FC<RangeSliderProps> = ({
  value: [left, right],
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  ariaLabelLeft,
  ariaLabelRight,
  ariaLabelGroup,
  ariaValueTextLeft,
  ariaValueTextRight,
  className,
  trackClassName,
  railClassName,
  rangeClassName,
  thumbLeftClassName,
  thumbRightClassName,
  thumbLeftColor = '#6F87F1',
  thumbRightColor = '#6F87F1',
  tooltipLeft,
  tooltipRight,
  labelLeft,
  labelRight,
  flipLabelPositionLeft = false,
  flipLabelPositionRight = false,
  style = {},
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeThumb, setActiveThumb] = useState<'left' | 'right' | null>(null);
  const [hoverLeft, setHoverLeft] = useState(false);
  const [hoverRight, setHoverRight] = useState(false);

  const rangeSpan = max - min;
  const toPct = useCallback(
    (v: number) => (rangeSpan === 0 ? 0 : ((v - min) / rangeSpan) * 100),
    [min, rangeSpan],
  );
  const fromPct = useCallback(
    (pct: number) => {
      const raw = min + (pct / 100) * rangeSpan;
      if (step <= 0) return raw;
      const stepped = Math.round(raw / step) * step;
      return clamp(stepped, min, max);
    },
    [min, rangeSpan, step, max],
  );

  const valueFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return left;
      const rect = el.getBoundingClientRect();
      const pct = (clientX - rect.left) / rect.width;
      return fromPct(pct * 100);
    },
    [fromPct, left],
  );

  const handlePointerDown = useCallback(
    (which: 'left' | 'right') => (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setActiveThumb(which);
    },
    [disabled],
  );

  /**
   * On move: if we would drag past the other handle, clamp to the other's position and
   * switch activeThumb so the other handle "takes over" the drag.
   */
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (activeThumb === null || disabled) return;
      const raw = valueFromClientX(e.clientX);

      if (activeThumb === 'right') {
        if (raw < left) {
          onChange([clamp(raw, min, left), left]);
          setActiveThumb('left');
        } else {
          onChange([left, clamp(raw, left, max)]);
        }
      } else {
        if (raw > right) {
          onChange([right, clamp(raw, right, max)]);
          setActiveThumb('right');
        } else {
          onChange([clamp(raw, min, right), right]);
        }
      }
    },
    [activeThumb, disabled, left, right, min, max, valueFromClientX, onChange],
  );

  const handlePointerUp = useCallback(() => {
    setActiveThumb(null);
  }, []);

  const updateLeft = useCallback(
    (newLeft: number) => onChange([clamp(newLeft, min, right), right]),
    [min, right, onChange],
  );
  const updateRight = useCallback(
    (newRight: number) => onChange([left, clamp(newRight, left, max)]),
    [left, max, onChange],
  );

  const handleKeyDown = useCallback(
    (which: 'left' | 'right') => (e: React.KeyboardEvent) => {
      if (disabled) return;
      const isLeft = which === 'left';
      const current = isLeft ? left : right;
      const update = isLeft ? updateLeft : updateRight;
      const smallStep = step;
      const bigStep = Math.max(1, Math.floor((max - min) / 10));

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          update(current - (e.shiftKey ? bigStep : smallStep));
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          update(current + (e.shiftKey ? bigStep : smallStep));
          break;
        case 'Home':
          e.preventDefault();
          if (isLeft) updateLeft(min);
          else updateRight(left);
          break;
        case 'End':
          e.preventDefault();
          if (isLeft) updateLeft(right);
          else updateRight(max);
          break;
        default:
          break;
      }
    },
    [disabled, left, right, min, max, step, updateLeft, updateRight],
  );

  const leftPct = toPct(left);
  const rightPct = toPct(right);
  const rangeStart = leftPct;
  const rangeWidth = rightPct - leftPct;

  const defaultTrackHeight = 8;
  const defaultThumbSize = 20;
  const labelSpace = 34;

  return (
    <div
      className={className}
      style={{
        minHeight: defaultTrackHeight,
        paddingTop: labelSpace,
        paddingBottom: labelSpace,
        overflow: 'visible',
        ...style.root,
      }}
      role="group"
      aria-label={ariaLabelGroup}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        ref={trackRef}
        className={trackClassName}
        style={{
          position: 'relative',
          width: '100%',
          height: defaultTrackHeight,
          touchAction: 'none',
          overflow: 'visible',
          ...style.track,
        }}
      >
        {/* Rail: full-width background */}
        <div
          className={railClassName}
          data-range-slider="rail"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: defaultTrackHeight,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,0.12)',
            ...style.rail,
          }}
        />
        {/* Range: L→R gradient from left handle color to right handle color */}
        <div
          className={rangeClassName}
          data-range-slider="range"
          style={{
            position: 'absolute',
            left: `${rangeStart}%`,
            width: `${rangeWidth}%`,
            top: '50%',
            height: defaultTrackHeight,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            borderRadius: 4,
            background: `linear-gradient(to right, ${thumbLeftColor}, ${thumbRightColor})`,
            ...style.range,
          }}
        />
        {/* Left label — below handle by default; above when flipLabelPositionLeft */}
        {labelLeft != null && (
          <div
            data-range-slider="label"
            data-range-slider-label="left"
            style={{
              position: 'absolute',
              left: `${leftPct}%`,
              ...(flipLabelPositionLeft
                ? { bottom: '100%', marginBottom: 12 }
                : { top: '100%', marginTop: 12 }),
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: '0.875rem',
              lineHeight: 1.3,
              color: '#fff',
              pointerEvents: 'none',
              ...style.labelLeft,
            }}
          >
            {labelLeft}
          </div>
        )}
        {/* Left handle — centered on track */}
        <Tooltip
          title={tooltipLeft ?? ''}
          placement={flipLabelPositionLeft ? 'bottom' : 'top'}
          arrow
          open={!!tooltipLeft && (hoverLeft || activeThumb === 'left')}
          disableHoverListener
          disableFocusListener
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '0.9375rem',
                padding: '10px 14px',
              },
            },
          }}
        >
          <div
            role="slider"
            aria-label={ariaLabelLeft}
            aria-valuemin={min}
            aria-valuemax={right}
            aria-valuenow={left}
            aria-valuetext={ariaValueTextLeft?.(left)}
            aria-disabled={disabled}
            aria-orientation="horizontal"
            tabIndex={disabled ? -1 : 0}
            data-range-slider="thumb"
            data-range-slider-thumb="left"
            data-dragging={activeThumb === 'left' || undefined}
            className={thumbLeftClassName}
            style={{
              position: 'absolute',
              left: `${leftPct}%`,
              top: '50%',
              width: defaultThumbSize,
              height: defaultThumbSize,
              transform: 'translate(-50%, -50%)',
              cursor: disabled ? 'default' : activeThumb === 'left' ? 'grabbing' : 'grab',
              touchAction: 'none',
              userSelect: 'none',
              borderRadius: '50%',
              backgroundColor: thumbLeftColor,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              ...style.thumbLeft,
            }}
            onPointerDown={handlePointerDown('left')}
            onPointerEnter={() => setHoverLeft(true)}
            onPointerLeave={() => setHoverLeft(false)}
            onKeyDown={handleKeyDown('left')}
          />
        </Tooltip>
        {/* Right label — below handle by default; above when flipLabelPositionRight */}
        {labelRight != null && (
          <div
            data-range-slider="label"
            data-range-slider-label="right"
            style={{
              position: 'absolute',
              left: `${rightPct}%`,
              ...(flipLabelPositionRight
                ? { bottom: '100%', marginBottom: 12 }
                : { top: '100%', marginTop: 12 }),
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: '0.875rem',
              lineHeight: 1.3,
              color: '#fff',
              pointerEvents: 'none',
              ...style.labelRight,
            }}
          >
            {labelRight}
          </div>
        )}
        {/* Right handle — centered on track */}
        <Tooltip
          title={tooltipRight ?? ''}
          placement={flipLabelPositionRight ? 'bottom' : 'top'}
          arrow
          open={!!tooltipRight && (hoverRight || activeThumb === 'right')}
          disableHoverListener
          disableFocusListener
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '0.9375rem',
                padding: '10px 14px',
              },
            },
          }}
        >
          <div
            role="slider"
            aria-label={ariaLabelRight}
            aria-valuemin={left}
            aria-valuemax={max}
            aria-valuenow={right}
            aria-valuetext={ariaValueTextRight?.(right)}
            aria-disabled={disabled}
            aria-orientation="horizontal"
            tabIndex={disabled ? -1 : 0}
            data-range-slider="thumb"
            data-range-slider-thumb="right"
            data-dragging={activeThumb === 'right' || undefined}
            className={thumbRightClassName}
            style={{
              position: 'absolute',
              left: `${rightPct}%`,
              top: '50%',
              width: defaultThumbSize,
              height: defaultThumbSize,
              transform: 'translate(-50%, -50%)',
              cursor: disabled ? 'default' : activeThumb === 'right' ? 'grabbing' : 'grab',
              touchAction: 'none',
              userSelect: 'none',
              borderRadius: '50%',
              backgroundColor: thumbRightColor,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              ...style.thumbRight,
            }}
            onPointerDown={handlePointerDown('right')}
            onPointerEnter={() => setHoverRight(true)}
            onPointerLeave={() => setHoverRight(false)}
            onKeyDown={handleKeyDown('right')}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default RangeSlider;
